import { useState } from 'react'
import { ChevronDown, ChevronRight, Copy, Check } from 'lucide-react'
import { guideStructure } from '@/data/reportContent'
import Button from '@/components/Button'

const Guide = () => {
  const [expandedSections, setExpandedSections] = useState(new Set(['introduction']))
  const [copiedCode, setCopiedCode] = useState('')

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(code)
      setTimeout(() => setCopiedCode(''), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  const codeExamples = {
    setup: `# Install VS Code extensions
# 1. GitHub Copilot
# 2. GitHub Copilot Chat
# 3. Language-specific extensions

# Configure Copilot
# Settings → "chat.agent.enabled" → true`,
    
    projectStructure: `Set up a complete project structure for [your project description].
Include:
- Proper folder organization
- Configuration files
- Package management setup
- Development scripts
- Basic documentation
- Git initialization`,
    
    featureRequest: `Implement [feature name] that allows users to [functionality].
Requirements:
- [Requirement 1]
- [Requirement 2]
- [Requirement 3]

Follow established patterns in the codebase and ensure:
- Proper error handling
- Input validation
- Responsive design (if applicable)
- Documentation`,
    
    codeReview: `Review the recent changes and suggest improvements for:
- Code quality and maintainability
- Performance optimizations
- Security considerations
- Testing coverage`
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Complete GitHub Copilot Agent Guide
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Master AI-powered development with this comprehensive guide covering everything from setup to advanced automation patterns.
          </p>
        </div>

        {/* Navigation */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Table of Contents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {guideStructure.map((section, index) => (
              <button
                key={section.id}
                onClick={() => toggleSection(section.id)}
                className="flex items-center text-left p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="text-lg mr-2">{section.icon}</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {index + 1}. {section.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Guide Sections */}
        <div className="space-y-6">
          {guideStructure.map((section, index) => (
            <div
              key={section.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">{section.icon}</span>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {index + 1}. {section.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                      {section.description}
                    </p>
                  </div>
                </div>
                {expandedSections.has(section.id) ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {expandedSections.has(section.id) && (
                <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="pt-4">
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      {section.content.overview}
                    </p>

                    {section.content.principles && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Core Principles:</h4>
                        <ul className="space-y-2">
                          {section.content.principles.map((principle) => (
                            <li key={principle} className="flex items-start">
                              <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3"></div>
                              <span className="text-gray-700 dark:text-gray-300">{principle}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {section.content.features && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Key Features:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {section.content.features.map((feature) => (
                            <div key={feature} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                              <div className="w-2 h-2 bg-accent-500 rounded-full mr-3"></div>
                              <span className="text-gray-700 dark:text-gray-300 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {section.content.steps && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Setup Steps:</h4>
                        <div className="space-y-3">
                          {section.content.steps.map((step, stepIndex) => (
                            <div key={step} className="flex items-start">
                              <div className="w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5">
                                {stepIndex + 1}
                              </div>
                              <span className="text-gray-700 dark:text-gray-300">{step}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {section.content.modes && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Available Modes:</h4>
                        <div className="space-y-3">
                          {section.content.modes.map((mode) => (
                            <div key={mode} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-l-4 border-blue-500">
                              <span className="text-gray-700 dark:text-gray-300">{mode}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {section.content.phases && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Development Phases:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {section.content.phases.map((phase, phaseIndex) => (
                            <div key={phase} className="p-4 bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg">
                              <div className="flex items-center mb-2">
                                <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold mr-2">
                                  {phaseIndex + 1}
                                </div>
                                <span className="font-medium text-gray-900 dark:text-white">{phase}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {section.content.patterns && (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Best Practices:</h4>
                        <div className="space-y-2">
                          {section.content.patterns.map((pattern) => (
                            <div key={pattern} className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                              <span className="text-gray-700 dark:text-gray-300">{pattern}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Code Examples */}
                    {section.id === 'setup' && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Setup Commands:</h4>
                        <div className="relative">
                          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                            <code>{codeExamples.setup}</code>
                          </pre>
                          <button
                            onClick={() => copyCode(codeExamples.setup)}
                            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
                          >
                            {copiedCode === codeExamples.setup ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {section.id === 'jumpstart' && (
                      <div className="mt-6 space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Project Structure Prompt:</h4>
                          <div className="relative">
                            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                              <code>{codeExamples.projectStructure}</code>
                            </pre>
                            <button
                              onClick={() => copyCode(codeExamples.projectStructure)}
                              className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
                            >
                              {copiedCode === codeExamples.projectStructure ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Feature Implementation Prompt:</h4>
                          <div className="relative">
                            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                              <code>{codeExamples.featureRequest}</code>
                            </pre>
                            <button
                              onClick={() => copyCode(codeExamples.featureRequest)}
                              className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
                            >
                              {copiedCode === codeExamples.featureRequest ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {section.id === 'workflows' && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Code Review Prompt:</h4>
                        <div className="relative">
                          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                            <code>{codeExamples.codeReview}</code>
                          </pre>
                          <button
                            onClick={() => copyCode(codeExamples.codeReview)}
                            className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white transition-colors"
                          >
                            {copiedCode === codeExamples.codeReview ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 dark:from-indigo-700 dark:via-purple-700 dark:to-blue-700 rounded-xl p-8 text-center shadow-xl">
          <h3 className="text-2xl font-bold mb-4 text-white">Ready to Get Started?</h3>
          <p className="text-indigo-100 mb-6 font-medium">
            Follow our quick start guide to begin your AI-powered development journey.
          </p>
          <Button 
            variant="secondary" 
            size="lg"
            onClick={() => window.location.href = '/quick-start'}
            className="bg-white text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 border-0 shadow-lg"
          >
            Start Quick Guide
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Guide
